import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { toast } from 'sonner';
import {
  Clock,
  Plus,
  Edit2,
  Trash2,
  Building2,
  MapPin,
  Calendar,
  Loader2,
  Sun,
  Moon,
  Coffee } from
'lucide-react';


const DAYS_OF_WEEK = [
{ key: 'monday', label: 'Monday', short: 'Mon' },
{ key: 'tuesday', label: 'Tuesday', short: 'Tue' },
{ key: 'wednesday', label: 'Wednesday', short: 'Wed' },
{ key: 'thursday', label: 'Thursday', short: 'Thu' },
{ key: 'friday', label: 'Friday', short: 'Fri' },
{ key: 'saturday', label: 'Saturday', short: 'Sat' },
{ key: 'sunday', label: 'Sunday', short: 'Sun' }];


const DEFAULT_SCHEDULE = {
  monday: { enabled: true, start: '08:00', end: '18:00', breaks: [{ start: '12:00', end: '13:00' }] },
  tuesday: { enabled: true, start: '08:00', end: '18:00', breaks: [{ start: '12:00', end: '13:00' }] },
  wednesday: { enabled: true, start: '08:00', end: '18:00', breaks: [{ start: '12:00', end: '13:00' }] },
  thursday: { enabled: true, start: '08:00', end: '18:00', breaks: [{ start: '12:00', end: '13:00' }] },
  friday: { enabled: true, start: '08:00', end: '18:00', breaks: [{ start: '12:00', end: '13:00' }] },
  saturday: { enabled: false, start: '08:00', end: '12:00', breaks: [] },
  sunday: { enabled: false, start: '08:00', end: '12:00', breaks: [] }
};

const TIMEZONES = [
{ value: 'America/Sao_Paulo', label: 'São Paulo (BRT/BRST)' },
{ value: 'America/New_York', label: 'New York (EST/EDT)' },
{ value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
{ value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
{ value: 'America/Mexico_City', label: 'Mexico City (CST/CDT)' },
{ value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)' },
{ value: 'Europe/London', label: 'London (GMT/BST)' },
{ value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
{ value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
{ value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
{ value: 'UTC', label: 'UTC' }];


export default function WorkingHoursPage() {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(true);
  const [workingHours, setWorkingHours] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [locations, setLocations] = useState([]);
  const [holidayCalendars, setHolidayCalendars] = useState([]);

  // New config form
  const [newConfig, setNewConfig] = useState({
    name: '',
    timezone: 'America/Sao_Paulo',
    location_id: null,
    department: '',
    holiday_calendar_id: null,
    weekly_schedule: { ...DEFAULT_SCHEDULE }
  });

  // Load working hours configs
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('ezerpToken');

        // Load working hours
        const whResponse = await fetch(`${API_BASE}/api/calendar/config/working-hours`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (whResponse.ok) {
          const whData = await whResponse.json();
          setWorkingHours(whData);
        }

        // Load locations for dropdown
        try {
          const locResponse = await fetch(`${API_BASE}/api/wms/locations`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (locResponse.ok) {
            const locData = await locResponse.json();
            setLocations(locData);
          }
        } catch (e) {

          // Locations API might not exist
        }
        // Load holiday calendars
        try {
          const hcResponse = await fetch(`${API_BASE}/api/calendar/config/holiday-calendars`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (hcResponse.ok) {
            const hcData = await hcResponse.json();
            setHolidayCalendars(hcData);
          }
        } catch (e) {

          // Might fail
        }} catch (error) {
        console.error('Failed to load working hours:', error);
        toast.error('Failed to load working hours');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Create working hours config
  const handleCreateConfig = async () => {
    try {
      const token = localStorage.getItem('ezerpToken');
      const response = await fetch(`${API_BASE}/api/calendar/config/working-hours`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newConfig)
      });

      if (!response.ok) throw new Error('Failed to create');

      toast.success('Working hours configuration created');
      setShowCreateDialog(false);
      setNewConfig({
        name: '',
        timezone: 'America/Sao_Paulo',
        location_id: null,
        department: '',
        holiday_calendar_id: null,
        weekly_schedule: { ...DEFAULT_SCHEDULE }
      });

      // Refresh
      const whResponse = await fetch(`${API_BASE}/api/calendar/config/working-hours`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (whResponse.ok) {
        setWorkingHours(await whResponse.json());
      }
    } catch (error) {
      console.error('Failed to create config:', error);
      toast.error('Failed to create working hours configuration');
    }
  };

  // Update day schedule
  const updateDaySchedule = (day, field, value) => {
    setNewConfig((prev) => ({
      ...prev,
      weekly_schedule: {
        ...prev.weekly_schedule,
        [day]: {
          ...prev.weekly_schedule[day],
          [field]: value
        }
      }
    }));
  };

  // Toggle day enabled
  const toggleDayEnabled = (day) => {
    setNewConfig((prev) => ({
      ...prev,
      weekly_schedule: {
        ...prev.weekly_schedule,
        [day]: {
          ...prev.weekly_schedule[day],
          enabled: !prev.weekly_schedule[day].enabled
        }
      }
    }));
  };

  // Calculate total hours per week
  const calculateWeeklyHours = (schedule) => {
    let totalMinutes = 0;

    DAYS_OF_WEEK.forEach(({ key }) => {
      const day = schedule[key];
      if (day?.enabled) {
        const [startH, startM] = day.start.split(':').map(Number);
        const [endH, endM] = day.end.split(':').map(Number);
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        let dayMinutes = endMinutes - startMinutes;

        // Subtract breaks
        if (day.breaks) {
          day.breaks.forEach((brk) => {
            const [bStartH, bStartM] = brk.start.split(':').map(Number);
            const [bEndH, bEndM] = brk.end.split(':').map(Number);
            dayMinutes -= bEndH * 60 + bEndM - (bStartH * 60 + bStartM);
          });
        }

        totalMinutes += dayMinutes;
      }
    });

    return (totalMinutes / 60).toFixed(1);
  };

  return (
    <div className="p-6 space-y-6" data-testid="working-hours-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Clock className="w-7 h-7 text-green-600" />{t("workingHours", "Working Hours")}

          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t("configureWorkingHours", "Configure working hours schedules for locations and departments")}

          </p>
        </div>
        
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-config-btn">
          <Plus className="w-4 h-4 mr-1" />{t("newSchedule", "New Schedule")}

        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("configurations", "Configurations")}</p>
                <p className="text-2xl font-bold">{workingHours.length}</p>
              </div>
              <Clock className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("locations", "Locations")}</p>
                <p className="text-2xl font-bold">{locations.length}</p>
              </div>
              <MapPin className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("holidayCalendars", "Holiday Calendars")}</p>
                <p className="text-2xl font-bold">{holidayCalendars.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("defaultTimezone", "Default Timezone")}</p>
                <p className="text-lg font-bold">{t("soPaulo", "S\xE3o Paulo")}</p>
              </div>
              <Sun className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configurations List */}
      {loading ?
      <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-green-600" />
            </div>
          </CardContent>
        </Card> :
      workingHours.length === 0 ?
      <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Clock className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">{t("noWorkingHours", "No Working Hours Configured")}

            </h3>
              <p className="text-slate-500 mb-4">{t("createAWorking", "Create a working hours schedule for your locations and departments")}

            </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-1" />{t("createSchedule", "Create Schedule")}

            </Button>
            </div>
          </CardContent>
        </Card> :

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {workingHours.map((config) =>
        <Card key={config.id} data-testid={`working-hours-${config.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{config.name}</CardTitle>
                    <CardDescription>
                      {config.department && <span>{config.department} • </span>}
                      {config.timezone}
                    </CardDescription>
                  </div>
                  <Badge variant="outline">
                    {calculateWeeklyHours(config.weekly_schedule)}{t("hweek", "h/week")}
              </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-1">
                  {DAYS_OF_WEEK.map(({ key, short }) => {
                const day = config.weekly_schedule?.[key];
                const isEnabled = day?.enabled;

                return (
                  <div
                    key={key}
                    className={`text-center p-2 rounded ${
                    isEnabled ?
                    'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                    'bg-slate-100 dark:bg-slate-800 text-slate-400'}`
                    }>
                    
                        <div className="text-xs font-medium">{short}</div>
                        {isEnabled &&
                    <div className="text-xs mt-1">
                            {day.start?.slice(0, 5)}-{day.end?.slice(0, 5)}
                          </div>
                    }
                      </div>);

              })}
                </div>
              </CardContent>
            </Card>
        )}
        </div>
      }

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("createWorkingHours", "Create Working Hours Schedule")}</DialogTitle>
            <DialogDescription>{t("defineWorkingHours", "Define working hours for a location or department")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("scheduleName", "Schedule Name *")}</Label>
                <Input
                  value={newConfig.name}
                  onChange={(e) => setNewConfig((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Main Warehouse"
                  className="mt-1"
                  data-testid="new-config-name" />
                
              </div>
              
              <div>
                <Label>{t("timezone", "Timezone")}</Label>
                <Select
                  value={newConfig.timezone}
                  onValueChange={(v) => setNewConfig((prev) => ({ ...prev, timezone: v }))}>
                  
                  <SelectTrigger className="mt-1" data-testid="new-config-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) =>
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("department", "Department")}</Label>
                <Input
                  value={newConfig.department}
                  onChange={(e) => setNewConfig((prev) => ({ ...prev, department: e.target.value }))}
                  placeholder="e.g., Receiving, Shipping"
                  className="mt-1" />
                
              </div>
              
              <div>
                <Label>{t("holidayCalendar", "Holiday Calendar")}</Label>
                <Select
                  value={newConfig.holiday_calendar_id?.toString() || ''}
                  onValueChange={(v) => setNewConfig((prev) => ({
                    ...prev,
                    holiday_calendar_id: v ? parseInt(v) : null
                  }))}>
                  
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select calendar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">{t("none", "None")}</SelectItem>
                    {holidayCalendars.map((hc) =>
                    <SelectItem key={hc.id} value={hc.id.toString()}>
                        {hc.name} ({hc.year})
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Weekly Schedule */}
            <div>
              <Label className="text-base font-semibold">{t("weeklySchedule", "Weekly Schedule")}</Label>
              <div className="mt-3 space-y-3">
                {DAYS_OF_WEEK.map(({ key, label }) => {
                  const day = newConfig.weekly_schedule[key];

                  return (
                    <div key={key} className="flex items-center gap-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <Switch
                        checked={day.enabled}
                        onCheckedChange={() => toggleDayEnabled(key)}
                        data-testid={`toggle-${key}`} />
                      
                      
                      <div className="w-24 font-medium">{label}</div>
                      
                      {day.enabled ?
                      <>
                          <div className="flex items-center gap-2">
                            <Sun className="w-4 h-4 text-slate-400" />
                            <Input
                            type="time"
                            value={day.start}
                            onChange={(e) => updateDaySchedule(key, 'start', e.target.value)}
                            className="w-28" />
                          
                          </div>
                          
                          <span className="text-slate-400">{t("to", "to")}</span>
                          
                          <div className="flex items-center gap-2">
                            <Moon className="w-4 h-4 text-slate-400" />
                            <Input
                            type="time"
                            value={day.end}
                            onChange={(e) => updateDaySchedule(key, 'end', e.target.value)}
                            className="w-28" />
                          
                          </div>
                          
                          {day.breaks?.length > 0 &&
                        <Badge variant="outline" className="ml-2">
                              <Coffee className="w-3 h-3 mr-1" />
                              {day.breaks[0].start}-{day.breaks[0].end}
                            </Badge>
                        }
                        </> :

                      <span className="text-slate-400 italic">{t("notWorking", "Not working")}</span>
                      }
                    </div>);

                })}
              </div>
              
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-between">
                <span className="text-sm text-blue-700 dark:text-blue-300">{t("totalWorkingHours", "Total working hours per week")}

                </span>
                <Badge className="bg-blue-600">
                  {calculateWeeklyHours(newConfig.weekly_schedule)}{t("hours", "hours")}
                </Badge>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleCreateConfig}
              disabled={!newConfig.name}
              data-testid="create-config-submit">{t("createSchedule", "Create Schedule")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

}